import React, { FunctionComponent, useEffect, useState } from 'react';
import { Presentation } from './presentation';
import { login } from '../../../services/sessions';
import { getCsrfToken } from '../../../libs/csrfToken';
import { buildOtpLockedMessage } from '../../../libs/formatLockedUntil';

const OTP_VERIFY_PATH = '/hr_login/verify_otp';

type Props = {
  isModalOpen: boolean;
  redirectUrl: string;
  isLoginError?: boolean;
  flashMessage?: string;
  fromButton: string;
  recruitmentId?: number;
  eventContentId?: number;
  handleToggleLoginModal: () => void;
  handleChangeModal: (from: 'login' | 'register') => void;
  lineIcon: string;
};

export const Component: FunctionComponent<Props> = ({
  isModalOpen,
  redirectUrl,
  isLoginError,
  flashMessage,
  fromButton,
  recruitmentId,
  eventContentId,
  handleToggleLoginModal,
  handleChangeModal,
  lineIcon,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);

  useEffect(() => {
  if (isModalOpen) {
      const params = new URLSearchParams(window.location.search);
      const isFromLine = params.get('utm_source') === 'line';
      if (isFromLine) {
        setTimeout(() => {
          (document.activeElement as HTMLElement)?.blur();
        }, 100);
      }
    }
  }, [isModalOpen]);

  const handleOauthsLinkClick = (provider: string) => {
    if (fromButton === 'recruitment') {
      sessionStorage.setItem("route_type", "recruitment");
      sessionStorage.setItem("route_recruitment_id", String(recruitmentId));
    }
    if (fromButton === 'event') {
      sessionStorage.setItem("route_type", "event");
      sessionStorage.setItem("route_event_content_id", String(eventContentId));
    }
    window.location.href = `/oauths/${provider}?redirect_url=${redirectUrl}`;
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (fromButton === 'recruitment') {
      sessionStorage.setItem("route_type", "recruitment");
      sessionStorage.setItem("route_recruitment_id", String(recruitmentId));
    }
    if (fromButton === 'event') {
      sessionStorage.setItem("route_type", "event");
      sessionStorage.setItem("route_event_content_id", String(eventContentId));
    }

    setSubmitErrorMessage(null);
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const email = String(formData.get('email') ?? '');
    const password = String(formData.get('password') ?? '');

    try {
      const response = await login(email, password, redirectUrl, getCsrfToken());
      if (response.state === 'otp_pending') {
        // 企業アカウント: ヘッダーのログインモーダルから直接ログインした場合も、
        // 専用ページ(managementLoginと合流)に遷移してメールOTPへ進む(旧実装はここが無言で失敗していた)。
        window.location.href = `${OTP_VERIFY_PATH}?sent=1`;
      } else if (response.error_code === 'otp_locked') {
        // ロック中はOTP画面へ遷移せず、モーダル自身の既存エラー表示でメッセージを出す。
        setSubmitErrorMessage(buildOtpLockedMessage(response.locked_until));
      } else if (response.error_message) {
        setSubmitErrorMessage(response.error_message);
      } else if (response.redirect_url) {
        // redirect_urlはAPI側(determine_safe_redirect_url/url_safe?)で検証済みの値のみを使用する
        window.location.href = response.redirect_url;
      }
    } catch {
      setSubmitErrorMessage('ログインに失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onToggleLoginModal = () => {
    clearSessionRouteType();
    handleToggleLoginModal();
  };

  const onChangeModal = () => {
    clearSessionRouteType();
    handleChangeModal('login');
  };

  const clearSessionRouteType = () => {
    sessionStorage.removeItem('route_type');
  };

  return (
    <Presentation
      isModalOpen={isModalOpen}
      isLoginError={Boolean(submitErrorMessage) || isLoginError}
      flashMessage={submitErrorMessage ?? flashMessage}
      isSubmitting={isSubmitting}
      fromButton={fromButton}
      handleToggleLoginModal={onToggleLoginModal}
      handleChangeModal={onChangeModal}
      handleOauthsLinkClick={handleOauthsLinkClick}
      handleFormSubmit={handleFormSubmit}
      lineIcon={lineIcon}
    />
  );
};
